import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import * as moment from 'moment';
import { AuthService } from 'src/app/service/auth.service';
import { ReportApiService } from 'src/app/shared/services/customer-api/report-api.service';
import { ConfigService } from 'src/app/service/config.service';
import {
  DialogReportComponent,
  ReportType,
} from 'src/app/micro-components/dialog-report/dialog-report.component';
import getScoreTypeDetails from 'src/app/shared/utils/score-type-details';
import {
  INITIAL_REPORT_TYPE_STATE,
  REPORT_REGISTRY,
  ReportDefinition,
  ReportDeps,
  ReportRegistryCtx,
  ReportTypeState,
  isStatusAvailable,
  scopeOfContext,
} from './report-state.registry';

export type ReportEntryId = ReportType | 'score';

export interface ReportEntry {
  id: ReportEntryId;
  menuTitle: string;
  menuSubtitle$: Observable<string>;
  tooltip$: Observable<string>;
  disabled$: Observable<boolean>;
}

export interface EntityReportContext {
  mode: 'entity';
  entity: { type: string; alpha_id: string; name: string };
  folio: { name: string; content: string[] };
  customScore: string;
}

export interface QueryReportContext {
  mode: 'query';
}

export type ReportContext = EntityReportContext | QueryReportContext;

export interface ReportBoxVm {
  loading: boolean;
  reports: ReportEntry[];
}

@Injectable({ providedIn: 'root' })
export class ReportStateService {
  // Score state — kept hand-rolled because score is multi-state (button-visible,
  // score-type, investment-disabled gate) and score-type-dependent.
  private isReportButtonVisibleSubject = new BehaviorSubject<boolean>(true);
  private isLoadingReportSubject = new BehaviorSubject<boolean>(false);
  private isReportAvailableSubject = new BehaviorSubject<boolean>(false);
  private reportStatusSubject = new BehaviorSubject<string>(null);
  private reportScoreTypeSubject = new BehaviorSubject<string>(null);
  private investmentReportDisabledSubject = new BehaviorSubject<boolean>(false);

  isReportButtonVisible$ = this.isReportButtonVisibleSubject.asObservable();
  isLoadingReport$ = this.isLoadingReportSubject.asObservable();
  isReportAvailable$ = this.isReportAvailableSubject.asObservable();
  reportStatus$ = this.reportStatusSubject.asObservable();
  reportScoreType$ = this.reportScoreTypeSubject.asObservable();
  investmentReportDisabled$ =
    this.investmentReportDisabledSubject.asObservable();

  // Registry-driven state — one slot per (id, scope) row.
  private readonly registryStates = new Map<
    string,
    BehaviorSubject<ReportTypeState>
  >();

  constructor(
    private auth: AuthService,
    private reportApi: ReportApiService,
    private config: ConfigService,
    private dialog: MatDialog,
  ) {
    for (const def of REPORT_REGISTRY) {
      this.registryStates.set(
        this.stateKey(def),
        new BehaviorSubject<ReportTypeState>(INITIAL_REPORT_TYPE_STATE),
      );
    }
  }

  // Public entry: callers pass the full ctx, the service runs the score check
  // (entity mode only) and the registry checks for the matching scope.
  checkStatuses(ctx: ReportContext): void {
    if (ctx.mode === 'entity') {
      this.runScoreCheck(ctx);
    }

    const matchingDefs = this.matchingDefs(ctx);

    for (const def of matchingDefs) {
      this.setState(def, { ...INITIAL_REPORT_TYPE_STATE, loading: true });
    }

    if (this.config.appConfig.setup.reportsDisabled) {
      for (const def of matchingDefs) this.setState(def, { loading: false });
      return;
    }

    const deps = this.buildDeps();
    const ctxData = this.toRegistryCtx(ctx);

    for (const def of matchingDefs) {
      def.checkStatus(ctxData, deps).subscribe({
        next: (status) =>
          this.setState(def, {
            status,
            available: isStatusAvailable(status),
            loading: false,
          }),
        error: () => this.setState(def, { loading: false }),
      });
    }
  }

  getViewModel$(ctx$: Observable<ReportContext>): Observable<ReportBoxVm> {
    return ctx$.pipe(switchMap((ctx) => this.buildVm$(ctx)));
  }

  dispatchReport(id: ReportEntryId, ctx: ReportContext): void {
    if (id === 'score') {
      if (ctx.mode === 'query') return;
      this.openScoreDialog(ctx);
      return;
    }
    const def = this.matchingDefs(ctx).find((d) => d.id === id);
    if (!def) return;
    const state = this.getStateSubject(def).getValue();
    this.dialog.open(DialogReportComponent, {
      restoreFocus: false,
      data: def.buildDialogData(
        this.toRegistryCtx(ctx),
        state,
        this.buildDeps(),
      ),
    });
  }

  getReportTooltip(customScore: string): string {
    const reportStatus = this.reportStatusSubject.getValue();

    if (reportStatus === 'not_enough_data') {
      return 'Report not available. Not enough data.';
    } else if (reportStatus === 'report_is_not_portfolio_or_company') {
      return 'Report not available. Not a portfolio or company.';
    } else if (reportStatus === 'investment_report_not_portfolio') {
      return 'Report not available. Not a portfolio.';
    } else if (reportStatus === 'wrong_score_type') {
      return 'Report is not available for the selected score type.';
    } else if (this.investmentReportDisabledSubject.getValue()) {
      return 'Investment Report can only be generated for portfolios with at least 6 companies.';
    } else {
      const scoreLabel =
        getScoreTypeDetails(customScore).reportLabel ||
        getScoreTypeDetails(customScore).label;
      return `Get a PDF summary on top ${scoreLabel} Stories, Score Trends and Peer-Group Benchmarks.`;
    }
  }

  clearState(): void {
    this.isReportButtonVisibleSubject.next(true);
    this.isLoadingReportSubject.next(false);
    this.isReportAvailableSubject.next(false);
    this.reportStatusSubject.next(null);
    this.reportScoreTypeSubject.next(null);
    this.investmentReportDisabledSubject.next(false);
    for (const subj of this.registryStates.values()) {
      subj.next(INITIAL_REPORT_TYPE_STATE);
    }
  }

  // ---- registry plumbing ----

  private stateKey(def: ReportDefinition): string {
    return `${def.id}::${def.scope}`;
  }

  private getStateSubject(
    def: ReportDefinition,
  ): BehaviorSubject<ReportTypeState> {
    return this.registryStates.get(this.stateKey(def))!;
  }

  private setState(
    def: ReportDefinition,
    partial: Partial<ReportTypeState>,
  ): void {
    const subj = this.getStateSubject(def);
    subj.next({ ...subj.getValue(), ...partial });
  }

  private matchingDefs(ctx: ReportContext): ReportDefinition[] {
    const scope = scopeOfContext(
      ctx.mode,
      ctx.mode === 'entity' ? ctx.entity?.type : undefined,
    );
    return scope === null
      ? []
      : REPORT_REGISTRY.filter((d) => d.scope === scope);
  }

  private buildDeps(): ReportDeps {
    return {
      reportApi: this.reportApi,
      auth: this.auth,
      date: moment(new Date()).format('YYYY-MM-DD'),
    };
  }

  private toRegistryCtx(ctx: ReportContext): ReportRegistryCtx {
    if (ctx.mode === 'query') return {};
    return { entity: ctx.entity, folio: ctx.folio };
  }

  // ---- view model ----

  private buildVm$(ctx: ReportContext): Observable<ReportBoxVm> {
    const matchingDefs = this.matchingDefs(ctx);

    const score$: Observable<ReportEntry | null> =
      ctx.mode === 'entity' ? this.buildScoreEntry$(ctx) : of(null);

    const registryEntries$: Observable<(ReportEntry | null)[]> =
      matchingDefs.length === 0
        ? of([])
        : combineLatest(
            matchingDefs.map((def) => this.buildRegistryEntry$(def)),
          );

    const entries$ = combineLatest([score$, registryEntries$]).pipe(
      map(([score, registryEntries]) =>
        [score, ...registryEntries].filter((e): e is ReportEntry => e !== null),
      ),
    );

    const registryLoading$: Observable<boolean> =
      matchingDefs.length === 0
        ? of(false)
        : combineLatest(
            matchingDefs.map((def) =>
              this.getStateSubject(def).pipe(map((s) => s.loading)),
            ),
          ).pipe(map((arr) => arr.some(Boolean)));

    const loading$ =
      ctx.mode === 'query'
        ? registryLoading$
        : combineLatest([this.isLoadingReport$, registryLoading$]).pipe(
            map(([a, b]) => a || b),
          );

    return combineLatest([entries$, loading$]).pipe(
      map(([reports, loading]) => ({ loading, reports })),
    );
  }

  private buildRegistryEntry$(
    def: ReportDefinition,
  ): Observable<ReportEntry | null> {
    return this.getStateSubject(def).pipe(
      map((state) =>
        state.available
          ? {
              id: def.id,
              menuTitle: def.menuTitle,
              menuSubtitle$: of(
                def.menuSubtitle ? def.menuSubtitle(state) : def.tooltip(state),
              ),
              tooltip$: of(def.tooltip(state)),
              disabled$: of(false),
            }
          : null,
      ),
    );
  }

  private buildScoreEntry$(
    ctx: EntityReportContext,
  ): Observable<ReportEntry | null> {
    const entityType = ctx.entity?.type;
    if (entityType !== 'company' && entityType !== 'portfolio') {
      return of(null);
    }

    // Company: show only when score-report is available.
    // Portfolio: show whenever the report button is allowed (button visible);
    //   investment-disabled and not-yet-available surface via disabled$.
    const visible$ =
      entityType === 'company'
        ? this.isReportAvailable$
        : this.isReportButtonVisible$;

    const tooltip$ = combineLatest([
      this.reportStatus$,
      this.investmentReportDisabled$,
    ]).pipe(map(() => this.getReportTooltip(ctx.customScore)));

    const disabled$ =
      entityType === 'portfolio'
        ? combineLatest([
            this.isReportAvailable$,
            this.investmentReportDisabled$,
          ]).pipe(map(([avail, disabled]) => !avail || disabled))
        : of(false);

    return visible$.pipe(
      map((visible) =>
        visible
          ? {
              id: 'score' as const,
              menuTitle: 'Score Report',
              menuSubtitle$: tooltip$,
              tooltip$,
              disabled$,
            }
          : null,
      ),
    );
  }

  // ---- score check (unchanged behaviour, just renamed from checkReportStatus) ----

  private runScoreCheck(ctx: EntityReportContext): void {
    const { entity, folio, customScore } = ctx;
    this.isReportButtonVisibleSubject.next(true);
    this.isReportAvailableSubject.next(false);
    this.isLoadingReportSubject.next(true);
    this.investmentReportDisabledSubject.next(false);

    const date = moment(new Date()).format('YYYY-MM-DD');
    const reportScoreType = getScoreTypeDetails(customScore).apiType;
    this.reportScoreTypeSubject.next(reportScoreType);

    if (this.config.appConfig.setup.reportsDisabled) {
      this.isReportButtonVisibleSubject.next(false);
      this.isLoadingReportSubject.next(false);
      return;
    }

    if (entity?.type === 'company') {
      if (reportScoreType && reportScoreType !== 'investment') {
        this.reportApi
          .getReportStatus(entity.alpha_id, reportScoreType, date)
          .subscribe(
            (res) => {
              this.reportStatusSubject.next(res['status']);
              this.isReportAvailableSubject.next(
                res['status'] !== 'not_enough_data',
              );
              this.isLoadingReportSubject.next(false);
            },
            () => this.isLoadingReportSubject.next(false),
          );
      } else {
        this.isReportAvailableSubject.next(false);
        this.isLoadingReportSubject.next(false);
      }
    } else if (entity?.type === 'portfolio') {
      if (!reportScoreType) {
        this.isReportButtonVisibleSubject.next(false);
        this.isLoadingReportSubject.next(false);
        return;
      }
      const portfolioHasContent = folio?.content?.length > 0;
      if (!portfolioHasContent) {
        this.isReportButtonVisibleSubject.next(false);
        this.isLoadingReportSubject.next(false);
        return;
      }
      const portfolio = folio.name;
      const companies = folio.content.filter(
        (e) => e.split(':')[0] === 'company',
      );
      const investmentDisabled =
        reportScoreType === 'investment' && companies.length < 6;
      this.investmentReportDisabledSubject.next(investmentDisabled);
      if (investmentDisabled) {
        this.isReportAvailableSubject.next(true);
        this.isLoadingReportSubject.next(false);
        return;
      }
      this.reportApi
        .getReportStatusPortfolio(portfolio, companies, reportScoreType, date)
        .subscribe(
          (res) => {
            this.reportStatusSubject.next(res['status']);
            if (res['status'] !== 'not_enough_data') {
              this.isReportAvailableSubject.next(true);
            }
            this.isLoadingReportSubject.next(false);
          },
          () => this.isLoadingReportSubject.next(false),
        );
    } else {
      this.isReportButtonVisibleSubject.next(false);
      this.isLoadingReportSubject.next(false);
    }
  }

  // ---- score dialog ----

  private openScoreDialog(ctx: EntityReportContext): void {
    if (this.investmentReportDisabledSubject.getValue()) {
      return;
    }
    const reportScoreType = this.reportScoreTypeSubject.getValue();
    const { entity, folio, customScore } = ctx;
    if (entity.type === 'portfolio') {
      this.dialog.open(DialogReportComponent, {
        restoreFocus: false,
        data: {
          reportType: reportScoreType as ReportType,
          entityId: entity.alpha_id,
          portfolioName: entity.name,
          portfolioContent: folio.content.filter(
            (e) => e.split(':')[0] === 'company',
          ),
          titleLine1: entity.name,
          titleLine2:
            (getScoreTypeDetails(customScore).reportLabel ||
              getScoreTypeDetails(customScore).label) + ' Portfolio Report',
          messageLine:
            folio.content.filter((e) => e.split(':')[0] === 'company')
              .length !== folio.content.length
              ? "Your portfolio includes entities other than companies that won't be covered by the report."
              : null,
          status: this.reportStatusSubject.getValue(),
        },
      });
    } else {
      this.dialog.open(DialogReportComponent, {
        restoreFocus: false,
        data: {
          reportType: reportScoreType as ReportType,
          entityId: entity.alpha_id,
          titleLine1: entity.name,
          titleLine2: this.auth.getScoreName().name + ' Report',
          status: this.reportStatusSubject.getValue(),
        },
      });
    }
  }
}
